import {useId, useState} from 'react'
import {Link, useLocation} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {QuestionGlyph} from '@/components/ui/icons'
import {StepCard} from '@/components/manual/step-card'
import {Tag} from '@/components/ui/tag'
import {findSeccion, seccionDeRuta, type ManualSectionId} from '@/manual'

/** El «?» de U6: abre la sección del manual de la pantalla activa; sin badge ni punto, porque no es una pendiente. */
export function AyudaDePantalla({seccion}: {seccion?: ManualSectionId}) {
  const {t} = useTranslation()
  const {pathname} = useLocation()
  const [open, setOpen] = useState(false)
  const enCortoId = useId()
  const porRuta = seccionDeRuta(pathname)
  const found = findSeccion(seccion ?? porRuta.seccion)
  if (!found) return null
  const {seccion: s} = found
  // El override de la ruta no aplica si la pantalla forzó su sección por prop.
  const enCorto = (seccion ? undefined : porRuta.enCorto) ?? s.enCorto
  // El título viaja con las viñetas: pisar solo unas deja la hoja contradiciéndose.
  const titulo = (seccion ? undefined : porRuta.titulo) ?? s.titulo
  const label = t('common.ayudaPantalla.aria', {pantalla: titulo})

  return (
    <>
      <button
        type="button"
        data-testid="ayuda-de-pantalla"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className="topbar-link flex min-h-11 w-9 cursor-pointer items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-ring/50 md:min-h-0 md:h-9">
        <QuestionGlyph />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="ayuda-de-pantalla-hoja"
          aria-label={label}
          aria-describedby={enCortoId}
          className="top-0 right-0 left-auto h-dvh max-h-dvh w-full max-w-[30rem] translate-x-0 translate-y-0 gap-5 overflow-y-auto rounded-none border-y-0 border-r-0">
          <DialogHeader>
            <DialogTitle data-testid="ayuda-de-pantalla-titulo">{titulo}</DialogTitle>

            <p
              data-testid="ayuda-en-corto-rotulo"
              className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('common.ayudaPantalla.enCorto')}
            </p>
            <ul
              id={enCortoId}
              data-testid="ayuda-en-corto"
              className="space-y-1.5 text-[13px] leading-snug text-foreground">
              {enCorto.map((linea) => (
                <li key={linea} className="flex gap-2">
                  <span aria-hidden="true" className="mt-[0.45rem] h-1 w-1 shrink-0 rounded-full bg-primary" />
                  <span>{linea}</span>
                </li>
              ))}
            </ul>
          </DialogHeader>

          <Link
            to={`/manual/${s.id}`}
            data-testid="ayuda-de-pantalla-manual"
            className="text-[13px] font-semibold text-primary outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
            {t('common.ayudaPantalla.verManual')} →
          </Link>

          {/* `<details>` y no estado propio: el navegador da el teclado y la búsqueda en página. */}
          {s.operaciones.length > 0 ? (
            <details data-testid="ayuda-de-pantalla-pasos">
              <summary className="cursor-pointer text-[13px] font-semibold text-primary outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
                {t('common.ayudaPantalla.pasos')}
              </summary>
              <div className="mt-3 space-y-3">
                <p className="text-[13px] leading-snug text-muted-foreground">{s.resumen}</p>
                {s.operaciones.map((op, i) => (
                  <StepCard key={op.titulo} op={op} index={i} />
                ))}
              </div>
            </details>
          ) : (
            <>
              <p className="text-[13px] leading-snug text-muted-foreground">{s.resumen}</p>
              <Tag tone="neutral">{t('manual.underConstructionTag')}</Tag>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
